import { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import AddBessForCI from "./AddBessForCI";
import WebService from "../../utility/WebService";
import HelperService from "../../utility/HelperService";

const BessForCI = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [data, setData] = useState<any>(null);
  const [isLoading, setLoading] = useState(false);
  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });

  useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    setLoading(true);
    WebService.getAPI({
      action: `bess-ci/getContent`,
    })
      .then((res: any) => {
        const perm = res?.data?.permissions || {};
        setPagePermission({
          canView: perm.canView,
          canAdd: perm.canAdd,
          canEdit: perm.canEdit,
          canDelete: perm.canDelete,
        });
        setData(res.data.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const renderBlocks = (label: string, blocks: any[]) => {
    if (!blocks || blocks.length === 0) return null;

    return (
      <>
        <strong>{label}</strong>
        <div className="d-flex flex-wrap gap-3 mb-3 mt-1">
          {blocks.map((item: any, i: number) => (
            <div
              key={i}
              className="border rounded-3 p-3 bg-light"
              style={{ width: 280 }}
            >
              <div className="fw-semibold mb-1">{item.title}</div>
              {item.description && (
                <div className="text-muted" style={{ fontSize: 13 }}>
                  {item.description}
                </div>
              )}
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderTags = (label: string, blocks: any[]) => {
    if (!blocks || blocks.length === 0) return null;

    return (
      <>
        <strong>{label}</strong>
        <div className="d-flex flex-wrap gap-2 mb-3 mt-1">
          {blocks.map((item: any, i: number) => (
            <span
              key={i}
              className="badge bg-light text-dark border rounded-3 px-3 py-2 fw-normal"
              style={{ fontSize: 13 }}
            >
              {item.title}
            </span>
          ))}
        </div>
      </>
    );
  };

  return (
    <>
      <AddBessForCI
        show={showAddModal}
        handleClose={() => {
          setShowAddModal(false);
          getData();
        }}
        data={data}
      />

      <div className="events-main">
        <Card className="event-card">
          {/* PAGE HEADER */}
          <div className="d-flex justify-content-between align-items-center">
            <div className="page-title">BESS for C&amp;I Workgroup</div>
          </div>

          <div className="p-3">
            {isLoading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: 100 }}
              >
                <span
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></span>
              </div>
            ) : (
              <>
                <div className="d-flex justify-content-end mb-3">
                  {pagePermission.canAdd && (
                    <CustomButton
                      isLoading={false}
                      label={data ? "Edit" : "Add"}
                      onClick={() => setShowAddModal(true)}
                      style={{ width: 100 }}
                    />
                  )}
                </div>

                {data === null ? (
                  <div>No data found</div>
                ) : (
                  <>
                    {/* Logo */}
                    {data?.logo && (
                      <div className="mb-2 mt-2">
                        <img
                          src={data.logo}
                          alt="Logo"
                          style={{ objectFit: "contain", width: 300 }}
                          className="border rounded-3 p-2"
                        />
                      </div>
                    )}

                    {/* Title */}
                    {data?.title && (
                      <>
                        <strong>Title</strong>
                        <div className="mb-3 mt-1">{data.title}</div>
                      </>
                    )}

                    {/* Description */}
                    {data?.description && (
                      <>
                        <strong>Description</strong>
                        <div className="ql-snow mb-2">
                          <div
                            className="px-0 ql-editor"
                            dangerouslySetInnerHTML={{
                              __html: HelperService.getPreviewableHTML(
                                data.description
                              ),
                            }}
                          />
                        </div>
                      </>
                    )}

                    {/* Registration — shown as the button itself, link stays hidden */}
                    <strong>Registration Button</strong>
                    <div className="d-flex align-items-center gap-2 mb-3 mt-1">
                      {data?.registration_link &&
                      data?.is_registration_open !== false ? (
                        <a
                          href={data.registration_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary btn-sm"
                        >
                          {data?.registration_button_text ||
                            "Registration is Open"}
                        </a>
                      ) : (
                        <span className="btn btn-secondary btn-sm disabled">
                          {data?.registration_button_text ||
                            "Registration is Open"}
                        </span>
                      )}
                      <span
                        className={`badge ${
                          data?.is_registration_open === false
                            ? "bg-secondary"
                            : "bg-success"
                        }`}
                      >
                        {data?.is_registration_open === false
                          ? "Closed"
                          : "Open"}
                      </span>
                    </div>

                    {/* Launch */}
                    {(data?.launch_title || data?.launch_subtitle) && (
                      <>
                        <strong>Launch Details</strong>
                        <div className="mb-3 mt-1">
                          {[data?.launch_title, data?.launch_subtitle]
                            .filter(Boolean)
                            .join(" | ")}
                        </div>
                      </>
                    )}
                    {/* Core Focus */}
                    {data?.core_focus_description && (
                      <>
                        <strong>
                          {data?.core_focus_title || "Core Focus"}
                        </strong>
                        <div className="ql-snow mb-2">
                          <div
                            className="px-0 ql-editor"
                            dangerouslySetInnerHTML={{
                              __html: HelperService.getPreviewableHTML(
                                data.core_focus_description
                              ),
                            }}
                          />
                        </div>
                      </>
                    )}
                    {data?.core_focus_short_description && (
                      <div className="ql-snow mb-2">
                        <div
                          className="px-0 ql-editor"
                          dangerouslySetInnerHTML={{
                            __html: HelperService.getPreviewableHTML(
                              data.core_focus_short_description
                            ),
                          }}
                        />
                      </div>
                    )}

                    {/* Repeatable sections */}
                    {renderBlocks("Who is this for?", data?.audiences)}
                    {renderTags("Criteria to Join the Group", data?.criteria)}
                    {renderTags(
                      "Value the Platform will Deliver",
                      data?.values
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </Card>
      </div>
    </>
  );
};

export default BessForCI;
